/**
 * First pipeline step: applies the tick's player commands to state. Each
 * command is checked against the sender's own entities and the shared
 * placement/spawn/promotion checks, isolated (one that throws is dropped)
 * and never depends on state hidden from the sender.
 */
import { getHexTileMapKey } from '../hexmap/model/keys';
import { getTile } from '../hexmap/model/map';
import type { HexTileMapKey } from '../hexmap/model/types';
import { nearestReachable, pathTo, type Reachable, reachableFrom } from '../hexmap/pathfinding';
import { canTakeJob, resolveJob } from './assign';
import { milliCost, toMilli } from './fixed';
import { levelDef } from './levels';
import {
  addHeading,
  blockersFor,
  deployUnit,
  forget,
  isMilitary,
  nearestFree,
  type Occupancy,
  occupancyOf,
  SETTLE_RADIUS,
  stopKind,
  storeWithRoom,
} from './occupancy';
import { canCancel, cancelRefund, canFoundCity, canPlace, canUpgrade, canWatch } from './placement';
import { handlePromote, handlePromoteInCity } from './promotion';
import { redirect } from './redirect';
import { isHidden } from './spies';
import { mintBuildingId } from './state';
import { deposit, gatherDepositCity, holdsStock, spend } from './stock';
import { formGroup, maintainGroups } from './systems/formation';
import { foundCity } from './systems/founding';
import { canHeal } from './systems/heal';
import {
  advanceQueue,
  attackTargetOf,
  canAttack,
  canHit,
  endFight,
  MAX_FOCUS_QUEUE,
} from './systems/pursuit';
import { trySpawnUnit } from './systems/spawn';
import { handleBlend, handleExpose } from './systems/spies';
import { cancelTraining } from './systems/training';
import { cancelUpgrade } from './systems/upgrades';
import { eliminatePlayer } from './systems/victory';
import { mintOrder, territoryOwners } from './territory';
import type {
  BuildingType,
  Cell,
  Command,
  EntityId,
  PlayerId,
  SimContext,
  SimState,
  Stance,
  SystemFn,
  Unit,
} from './types';
import { canTrain, setXp, trainCost } from './veterancy';
import { computeVision } from './vision';
import { cleanTargets } from './workTargets';

/**
 * Where `unit` should end a move aimed at `goal`: a military unit sent to its
 * own city goes into that city's barracks while it has room; otherwise the
 * goal when the unit can stop there, else the nearest free tile around it
 * (see occupancy.ts). A hidden unit (spies.ts) holds no tile, so it stops on
 * the goal whoever is heading there, unless a building or a still unit it
 * sees is on it (so it never shares a tile it could blend in on). Undefined
 * when nothing reachable is free.
 */
function destinationFor(
  state: SimState,
  ctx: SimContext,
  occ: Occupancy,
  unit: Unit,
  goal: Cell,
  reach: Reachable,
  owners: () => ReadonlyMap<HexTileMapKey, EntityId>
): Cell | undefined {
  const { rules } = ctx;
  const who = { id: unit.id, owner: unit.owner, type: unit.type };
  const key = getHexTileMapKey(goal);
  const here = occ.buildings.get(key);
  if (isHidden(rules, unit) && !here && !occ.standing.has(key) && reach.dist.has(key)) return goal;
  if (isMilitary(rules, unit.type) && here?.owner === unit.owner && holdsStock(rules, here)) {
    const store = storeWithRoom(state, rules, occ, who, here.id, owners());
    if (store && reach.dist.has(getHexTileMapKey(store.pos))) return store.pos;
  }
  if (reach.dist.has(key) && stopKind(rules, occ, who, goal, true) !== null) return goal;
  return nearestFree(ctx, occ, who, goal, 1, SETTLE_RADIUS, reach);
}

/**
 * Grouping on a move: a lone military unit ordered onto a tile where an own
 * military unit stands joins that unit's group (made if needed) and leads it,
 * except a healer, which follows (it would otherwise drag the group to the
 * wounded, tend.ts); otherwise each group with a member in the order is now
 * led by its last one listed.
 */
function regroup(state: SimState, ctx: SimContext, units: readonly Unit[], target: Cell): void {
  const [only] = units;
  if (units.length === 1 && isMilitary(ctx.rules, only.type)) {
    const mate = Object.values(state.units)
      .filter(
        (u) =>
          u.id !== only.id &&
          u.owner === only.owner &&
          !u.inside &&
          !u.path &&
          u.pos.q === target.q &&
          u.pos.r === target.r &&
          isMilitary(ctx.rules, u.type)
      )
      .sort((a, b) => mintOrder(a.id) - mintOrder(b.id))[0];
    if (mate) {
      const group = mate.groupId !== undefined ? state.groups?.[mate.groupId] : undefined;
      const follows = canHeal(ctx.rules, only);
      if (group && mate.groupId !== undefined) {
        only.groupId = mate.groupId;
        if (!follows) group.leader = only.id;
      } else {
        formGroup(state, [mate, only], follows ? mate : only);
      }
      return;
    }
  }
  for (const u of units) {
    const group = u.groupId !== undefined ? state.groups?.[u.groupId] : undefined;
    if (group) group.leader = u.id;
  }
}

/**
 * Units ordered together keep to the slowest one's speed until they arrive or
 * their target is replaced (follow-up, blocker, resume, guard; `Unit.pace`,
 * movement.ts `paceOf`), so cavalry don't reach the target
 * alone; a unit ordered on its own moves at its own speed.
 */
function setPace(ctx: SimContext, units: readonly Unit[]): void {
  let slowest = Number.POSITIVE_INFINITY;
  for (const u of units) {
    slowest = Math.min(slowest, ctx.rules.units[u.type]?.speedMultiplier ?? slowest);
  }
  for (const u of units) {
    if (units.length > 1 && Number.isFinite(slowest)) u.pace = toMilli(slowest);
    else delete u.pace;
  }
}

/**
 * Moves units to `target`. The last unit listed leads: it heads for the
 * target (or the nearest free tile around it), and the rest take the nearest
 * free tiles around the leader's destination (formation). A unit inside a
 * building leaves it. Paths go around the enemy units and rival buildings
 * the player can see. See `destinationFor` and `regroup`.
 */
function handleMove(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  unitIds: EntityId[],
  target: Cell
): void {
  if (!getTile(ctx.terrain, target)) return;
  // Deduped: each id costs a path search, so repeats would only burn host time.
  const units = [...new Set(unitIds)]
    .map((id) => state.units[id])
    .filter((u): u is Unit => !!u && u.owner === playerId && !!ctx.rules.units[u.type]);
  if (units.length === 0) return;
  regroup(state, ctx, units, target);
  setPace(ctx, units);

  // Only what the player can see decides where the formation stops.
  const vision = computeVision(state, ctx.rules, playerId);
  const occ = occupancyOf(state, ctx.rules, { player: playerId, vision });
  const blocked = blockersFor(state, ctx.rules, playerId, vision);
  for (const u of units) forget(occ, u);
  let owners: ReadonlyMap<HexTileMapKey, EntityId> | undefined;
  const ownersOnce = () => {
    owners ??= territoryOwners(state, ctx.rules);
    return owners;
  };
  const leader = units[units.length - 1];
  let anchor: Cell = target;
  for (const unit of [leader, ...units.slice(0, -1)]) {
    const terrain = ctx.rules.units[unit.type]?.terrain ?? [];
    // A manual order takes the unit off automation, and a spy out of its disguise.
    delete unit.auto;
    delete unit.disguise;
    // An impassable or unreachable tile (e.g. water) still gets an answer:
    // the unit heads for the nearest reachable tile. Otherwise rejecting the
    // order would reveal what's under the fog.
    // One search per unit: it answers both where it can get and the path there.
    const reach = reachableFrom(ctx.terrain, unit.pos, terrain, blocked);
    const goal = nearestReachable(reach, anchor);
    if (goal === null) continue;
    const dest = destinationFor(state, ctx, occ, unit, goal, reach, ownersOnce) ?? goal;
    const path = pathTo(reach, reach.dist.has(getHexTileMapKey(dest)) ? dest : goal);
    const end = path[path.length - 1];
    if (unit === leader) anchor = end;

    delete unit.order;
    // Keeps the step under way (redirect.ts): re-ordering never snaps the unit back.
    const stop = redirect(state, unit, path);
    // Rerouted: remember the tile the player picked, for the move marker.
    if (path.length > 1 && unit === leader && (end.q !== target.q || end.r !== target.r)) {
      unit.order = { kind: 'move', target: { q: target.q, r: target.r } };
    }
    addHeading(occ, unit, stop);
  }
}

/**
 * Attack-move: a move (same pathing, pace, groups and formation) after which
 * every aggressive fighter also carries an `attackMove` order, so pursuit.ts
 * turns it on the visible enemies it meets on the way and resumes the walk
 * afterwards. A unit with a stance (hold fires at what's in range anyway,
 * passive never starts a fight) and non-fighters just move. Units already on
 * the target tile have no path and so no order.
 */
function handleAttackMove(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  unitIds: EntityId[],
  target: Cell
): void {
  if (!getTile(ctx.terrain, target)) return;
  handleMove(state, ctx, playerId, unitIds, target);
  for (const id of new Set(unitIds)) {
    const unit = state.units[id];
    if (!unit || unit.owner !== playerId || !canAttack(ctx.rules, unit)) continue;
    if (unit.stance || !unit.path || (unit.order && unit.order.kind !== 'move')) continue;
    unit.order = { kind: 'attackMove', target: { q: target.q, r: target.r } };
  }
}

/**
 * Orders units at an enemy unit or building. Fighters get an attack order
 * (the pursuit system closes them to a tile in range, and combat hits the
 * target first); ones inside a building come out first. Healers sent with
 * fighters follow them instead of walking into the fight: one not already
 * in a group led by one of those fighters joins the last fighter's group
 * (made if needed, the fighter leading it), so it trails the fight
 * (formation.ts) and tends the wounded around it (tend.ts). The rest just
 * walk to its tile. An own or missing target (a remembered building may be
 * gone) or a rival unit the player can't see (e.g. sent at from an old
 * notification) makes it a move to `cell`, its last known tile, so the order
 * neither fails silently nor reveals what's under the fog.
 *
 * With `queue` (shift-click), a fighter already on a manual attack order
 * (a guard call is automatic, so it counts as none) appends the target to
 * its focus queue instead (no duplicates, not the current target, at most
 * MAX_FOCUS_QUEUE; extras are ignored); the other fighters attack it now.
 * Units that can't hit it are left alone, as is everything when the target
 * isn't a valid one (no move fallback: a queued click mustn't walk units).
 */
function handleAttack(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  unitIds: EntityId[],
  targetId: EntityId,
  cell: Cell,
  queue: boolean
): void {
  const target = attackTargetOf(state, ctx.rules, targetId);
  if (
    !target ||
    target.owner === playerId ||
    (target.id in state.units &&
      !computeVision(state, ctx.rules, playerId).has(getHexTileMapKey(target.pos)))
  ) {
    if (!queue) handleMove(state, ctx, playerId, unitIds, cell);
    return;
  }
  let occ: Occupancy | undefined;
  const walkers: Unit[] = [];
  let lastFighter: Unit | undefined;
  const fighterIds = new Set<EntityId>();
  for (const unitId of new Set(unitIds)) {
    const unit = state.units[unitId];
    if (!unit || unit.owner !== playerId) continue;
    // A unit that can't hit this kind of target (a ram and a unit) walks too.
    if (!canAttack(ctx.rules, unit) || !canHit(ctx.rules, unit.type, target.id in state.units)) {
      if (!queue) walkers.push(unit);
      continue;
    }
    const prev = unit.order;
    if (queue && prev?.kind === 'attack' && prev.guard === undefined) {
      const queued = prev.queue ?? [];
      if (
        prev.targetId !== targetId &&
        !queued.includes(targetId) &&
        queued.length < MAX_FOCUS_QUEUE
      ) {
        prev.queue = [...queued, targetId];
      }
      continue;
    }
    lastFighter = unit;
    fighterIds.add(unit.id);
    delete unit.auto;
    // Out of the building to fight: onto a free tile around it when there is
    // one (pursuit takes over from there), else straight from its tile.
    if (unit.inside) {
      occ ??= occupancyOf(state, ctx.rules);
      deployUnit(state, ctx, occ, unit);
      delete unit.inside;
    }
    unit.order = { kind: 'attack', targetId };
  }
  setPace(
    ctx,
    [...fighterIds].map((id) => state.units[id])
  );
  const healers = lastFighter ? walkers.filter((u) => canHeal(ctx.rules, u)) : [];
  if (lastFighter && healers.length > 0) {
    const leaderOf = (u: Unit) =>
      u.groupId !== undefined ? state.groups?.[u.groupId]?.leader : undefined;
    const strays = healers.filter((h) => !fighterIds.has(leaderOf(h) ?? ''));
    if (strays.length > 0) {
      const group =
        lastFighter.groupId !== undefined ? state.groups?.[lastFighter.groupId] : undefined;
      if (group && lastFighter.groupId !== undefined) {
        for (const h of strays) h.groupId = lastFighter.groupId;
        if (!fighterIds.has(group.leader)) group.leader = lastFighter.id;
      } else {
        formGroup(state, [...strays, lastFighter], lastFighter);
      }
      maintainGroups(state);
    }
    // Formation trails them after the fighters (whose paths pursuit plans).
    for (const h of healers) {
      delete h.auto;
      delete h.order;
      redirect(state, h, [h.pos]);
      if (h.inside) {
        occ ??= occupancyOf(state, ctx.rules);
        deployUnit(state, ctx, occ, h);
        delete h.inside;
      }
    }
  }
  const rest = walkers.filter((u) => !healers.includes(u)).map((u) => u.id);
  if (rest.length > 0) handleMove(state, ctx, playerId, rest, target.pos);
}

/**
 * Ends the focus of own units on a manual attack order (guard calls are
 * automatic and left alone). With `targetId`, drops it from the queue; if it
 * is the current target the unit takes the next valid queued one, else the
 * focus ends (no follow-up: the player took the target off the list).
 * Without, the current target and the queue go. Ending the focus stands the
 * unit, or, in an attack-move (`onward`), sends it on to its destination as
 * pursuit does once a fight is over (an Esc doesn't cancel the march).
 */
function handleUnfocus(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  unitIds: EntityId[],
  targetId: EntityId | undefined
): void {
  const vision = computeVision(state, ctx.rules, playerId);
  for (const unitId of new Set(unitIds)) {
    const unit = state.units[unitId];
    const order = unit?.order;
    if (!unit || unit.owner !== playerId || order?.kind !== 'attack') continue;
    if (order.guard !== undefined) continue;
    if (targetId !== undefined) {
      if (order.targetId !== targetId) {
        if (order.queue?.includes(targetId)) {
          order.queue = order.queue.filter((id) => id !== targetId);
          if (order.queue.length === 0) delete order.queue;
        }
        continue;
      }
      if (order.queue) {
        order.queue = order.queue.filter((id) => id !== targetId);
        if (order.queue.length === 0) delete order.queue;
      }
      if (advanceQueue(state, ctx.rules, unit, vision)) continue;
    }
    endFight(state, ctx, unit, blockersFor(state, ctx.rules, playerId, vision), 'attackMove');
    if (!unit.order) redirect(state, unit, [unit.pos]);
  }
}

/**
 * Places a construction site (cost paid up front). No unit is sent: an idle
 * builder standing on the site takes the job (construction system), and
 * automated workers seek out unclaimed sites (automation).
 */
function handlePlace(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  buildingType: BuildingType,
  target: Cell
): void {
  if (!canPlace(state, ctx, playerId, buildingType, target).ok) return;
  const def = ctx.rules.buildings[buildingType];
  if (!def) return;
  spend(state, playerId, milliCost(def.cost ?? {}));

  const id = mintBuildingId(state);
  state.buildings[id] = {
    id,
    owner: playerId,
    type: buildingType,
    pos: { q: target.q, r: target.r },
    hp: toMilli(def.hp),
    buildProgress: 0,
  };
}

function handleFoundCity(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  unitId: EntityId
): void {
  if (!canFoundCity(state, ctx, playerId, unitId).ok) return;
  foundCity(state, ctx, state.units[unitId]);
}

/**
 * Sends an own settler to `target` to found a city there on arrival (see
 * systems/founding.ts): a move (fog-safe, as any move) that leaves the
 * settler a `found` order, out of any group so formation doesn't pull it
 * away. Already standing there, it founds this tick.
 */
function handleFoundAt(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  unitId: EntityId,
  target: Cell
): void {
  const unit = state.units[unitId];
  if (!unit || unit.owner !== playerId) return;
  if (!ctx.rules.units[unit.type]?.actions.includes('found-city')) return;
  if (!getTile(ctx.terrain, target)) return;
  if (unit.groupId !== undefined) {
    delete unit.groupId;
    maintainGroups(state);
  }
  handleMove(state, ctx, playerId, [unitId], target);
  unit.order = { kind: 'found', target: { q: target.q, r: target.r } };
}

/** Points an own completed watchtower at a tile in its range, or (null) stops it watching. */
function handleWatch(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  buildingId: EntityId,
  target: Cell | null
): void {
  if (!canWatch(state, ctx, playerId, buildingId, target).ok) return;
  const b = state.buildings[buildingId];
  if (target === null) delete b.watch;
  else b.watch = { q: target.q, r: target.r };
}

/**
 * Sets what an own completed city's auto workers favour: `'balanced'`
 * (clears it) or a resource id of the rules (see automation.ts `pickWork`).
 */
function handleSetWorkFocus(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  cityId: EntityId,
  focus: string
): void {
  const city = state.buildings[cityId];
  if (!city || city.owner !== playerId || !holdsStock(ctx.rules, city)) return;
  if (focus === 'balanced') delete city.workFocus;
  else if (Object.hasOwn(ctx.rules.resources, focus)) city.workFocus = focus;
}

/** Sets an own completed city's worker targets per task (workTargets.ts); none left clears them. */
function handleSetWorkTargets(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  cityId: EntityId,
  targets: Record<string, number>
): void {
  const city = state.buildings[cityId];
  if (!city || city.owner !== playerId || city.buildProgress < 1 || !holdsStock(ctx.rules, city)) {
    return;
  }
  const clean = cleanTargets(ctx.rules, city, targets);
  if (clean) city.workTargets = clean;
  else delete city.workTargets;
}

/**
 * Starts a building's upgrade one level up: pays the cost now (kept in
 * `upgrading.paid` for a refund on cancel); the level, hp and growth follow
 * when the `upgrades` system finishes it (systems/upgrades.ts).
 */
function handleUpgrade(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  buildingId: EntityId
): void {
  const check = canUpgrade(state, ctx, playerId, buildingId);
  if (!check.ok) return;
  const b = state.buildings[buildingId];
  const def = ctx.rules.buildings[b.type];
  const next = def && levelDef(def, check.level);
  if (!next) return;
  const paid = milliCost(next.cost);
  spend(state, playerId, paid);
  b.upgrading = { level: check.level, progress: 0, paid };
}

/**
 * Sends own workers to a job (see assign.ts): pinned (the default), they get
 * a gather or build order with `pin` and leave automation until given
 * another order; a nudge (`pin` false) leaves them automated, heading there
 * first. Units that can't take the job are skipped; a rival or unknown
 * target does nothing. Paths go round what the player can see, like a move;
 * a job tile the unit can't reach or stop on is kept as the order (pinned)
 * and walked to when it opens up (automation.ts `stepAssigned`).
 */
function handleAssign(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  unitIds: EntityId[],
  target: Cell | EntityId,
  pin = true
): void {
  const check = resolveJob(state, ctx, playerId, target);
  if (!check.ok) return;
  const { job } = check;
  const units = [...new Set(unitIds)]
    .map((id) => state.units[id])
    .filter((u): u is Unit => !!u && u.owner === playerId && canTakeJob(ctx, u, job));
  if (units.length === 0) return;
  const goal = job.kind === 'build' ? job.building.pos : job.target;
  const goalKey = getHexTileMapKey(goal);
  const vision = computeVision(state, ctx.rules, playerId);
  const occ = occupancyOf(state, ctx.rules, { player: playerId, vision });
  const blocked = blockersFor(state, ctx.rules, playerId, vision);
  let regrouped = false;
  for (const unit of units) {
    // A nudge only reaches workers that are, or can be, automated.
    if (!pin && !unit.auto && ctx.rules.units[unit.type]?.automate !== 'work') continue;
    forget(occ, unit);
    // Out of its group, or formation and group pacing would drag it off the job.
    if (unit.groupId !== undefined) {
      delete unit.groupId;
      regrouped = true;
    }
    delete unit.disguise;
    delete unit.blocked;
    delete unit.pace;
    delete unit.order;
    if (pin) {
      delete unit.auto;
      unit.order =
        job.kind === 'build'
          ? { kind: 'build', buildingId: job.building.id, pin: true }
          : { kind: 'gather', target: { q: goal.q, r: goal.r }, pin: true };
    } else if (unit.auto) {
      delete unit.auto.phase;
      delete unit.auto.work;
      delete unit.auto.retryTick;
    } else if (ctx.rules.units[unit.type]?.automate === 'work') {
      unit.auto = {};
    }
    if (unit.pos.q === goal.q && unit.pos.r === goal.r) {
      redirect(state, unit, [unit.pos]);
      delete unit.auto?.target;
      continue;
    }
    const terrain = ctx.rules.units[unit.type]?.terrain ?? [];
    const reach = reachableFrom(ctx.terrain, unit.pos, terrain, blocked);
    if (!reach.dist.has(goalKey) || stopKind(ctx.rules, occ, unit, goal, true) === null) {
      redirect(state, unit, [unit.pos]);
      delete unit.auto?.target;
      continue;
    }
    redirect(state, unit, pathTo(reach, goal));
    addHeading(occ, unit, goal);
    if (unit.auto) unit.auto.target = { q: goal.q, r: goal.r };
  }
  if (regrouped) maintainGroups(state);
}

/** Makes an own completed city the home of the given own units. */
function handleSetHome(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  unitIds: EntityId[],
  cityId: EntityId
): void {
  const city = state.buildings[cityId];
  if (!city || city.owner !== playerId || !holdsStock(ctx.rules, city)) return;
  for (const id of new Set(unitIds)) {
    const unit = state.units[id];
    if (unit?.owner === playerId) unit.homeCityId = city.id;
  }
}

/**
 * Cancels an own unfinished site of a placed building (not a city site): it
 * is removed and `pacing.cancelRefund` of its cost goes back, deposited like
 * gathered resources (the city whose territory holds the site when it's the
 * owner's, else their nearest completed city; lost with no city).
 */
function handleCancel(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  buildingId: EntityId
): void {
  if (!canCancel(state, ctx.rules, playerId, buildingId)) return;
  const b = state.buildings[buildingId];
  delete state.buildings[buildingId];
  const owners = territoryOwners(state, ctx.rules);
  const city = gatherDepositCity(state, ctx.rules, playerId, b.pos, owners);
  if (city === undefined) return;
  for (const [res, amount] of Object.entries(cancelRefund(ctx.rules, b.type))) {
    deposit(state, city, res, toMilli(amount));
  }
}

/**
 * Hands units back to their type's automatic behaviour; they re-plan next
 * tick. A unit standing in one of its owner's city's territory makes that
 * city its home.
 */
function handleAutomate(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  unitIds: EntityId[]
): void {
  let owners: ReadonlyMap<HexTileMapKey, EntityId> | undefined;
  for (const unitId of new Set(unitIds)) {
    const unit = state.units[unitId];
    if (!unit || unit.owner !== playerId) continue;
    if (!ctx.rules.units[unit.type]?.automate) continue;
    owners ??= territoryOwners(state, ctx.rules);
    const city = state.buildings[owners.get(getHexTileMapKey(unit.pos)) ?? ''];
    if (city?.owner === playerId && holdsStock(ctx.rules, city)) unit.homeCityId = city.id;
    delete unit.order;
    redirect(state, unit, [unit.pos]);
    unit.auto = {};
  }
}

/**
 * Sets the stance of the given own units. Aggressive is the default and kept
 * omitted. Only fighters take a stance. A non-aggressive unit drops a guard
 * call and stands where it is; any other order is left alone. That includes
 * an in-flight follow-up or blocker attack, which looks the same as a manual
 * attack order: it ends with its target.
 */
function handleSetStance(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  unitIds: EntityId[],
  stance: Stance
): void {
  for (const unitId of new Set(unitIds)) {
    const unit = state.units[unitId];
    if (!unit || unit.owner !== playerId || !canAttack(ctx.rules, unit)) continue;
    if (stance === 'aggressive') delete unit.stance;
    else unit.stance = stance;
    const order = unit.order;
    if (stance !== 'aggressive' && order?.kind === 'attack' && order.guard !== undefined) {
      delete unit.order;
      redirect(state, unit, [unit.pos]);
    }
  }
}

/** Makes the given own units (two or more) one new group, led by the last listed. */
function handleGroup(state: SimState, playerId: PlayerId, unitIds: EntityId[]): void {
  const units = [...new Set(unitIds)]
    .map((id) => state.units[id])
    .filter((u): u is Unit => !!u && u.owner === playerId);
  if (units.length < 2) return;
  // Grouped units are under manual control.
  for (const u of units) delete u.auto;
  formGroup(state, units, units[units.length - 1]);
  maintainGroups(state);
}

/** Takes the given own units out of their groups (a group left with one member dissolves). */
function handleUngroup(state: SimState, playerId: PlayerId, unitIds: EntityId[]): void {
  for (const id of new Set(unitIds)) {
    const u = state.units[id];
    if (u?.owner === playerId) delete u.groupId;
  }
  maintainGroups(state);
}

/** Brings the given own units inside buildings out onto free tiles around them. */
function handleDeploy(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  unitIds: EntityId[]
): void {
  const occ = occupancyOf(state, ctx.rules);
  const ids = [...new Set(unitIds)].sort((a, b) => mintOrder(a) - mintOrder(b));
  for (const id of ids) {
    const u = state.units[id];
    if (u?.owner === playerId && u.inside) deployUnit(state, ctx, occ, u);
  }
}

/** Buys a unit its next rank (see `canTrain`): pays and sets its xp to that rank's threshold. */
function handleTrain(state: SimState, ctx: SimContext, playerId: PlayerId, unitId: EntityId): void {
  const check = canTrain(state, ctx.rules, playerId, unitId);
  if (!check.ok) return;
  const unit = state.units[unitId];
  spend(state, playerId, milliCost(trainCost(ctx.rules, check.rank)));
  const need = ctx.rules.veterancy.ranks[check.rank - 1];
  setXp(ctx.rules, unit, Math.max(unit.xp ?? 0, need));
}

function handleResign(state: SimState, playerId: PlayerId): void {
  const player = state.players[playerId];
  if (!player) return;
  // Marked before the eliminated check, so an eliminated host who later
  // leaves still hands the match-host role on (settings.ts matchHost).
  player.resigned = true;
  if (player.eliminated) return;

  eliminatePlayer(state, playerId);
}

/**
 * Command processing system. Iterates inputs in order and dispatches each
 * command; invalid inputs are silently dropped. Commands from peers are
 * validated at the wire (net/protocol isCommand), and each command is
 * additionally isolated so one that still throws can't stop the tick.
 * Does not touch state.tick or state.elapsed.
 */
const applyCommands: SystemFn = (
  state: SimState,
  ctx: SimContext,
  inputs: readonly import('./types').PlayerInput[],
  dt: number
): void => {
  for (const { playerId, command } of inputs) {
    try {
      applyCommand(state, ctx, playerId, command, dt);
    } catch {
      // Malformed command: drop it and carry on with the rest.
    }
  }
};

function applyCommand(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  command: Command,
  dt: number
): void {
  switch (command.type) {
    case 'move':
      handleMove(state, ctx, playerId, command.unitIds, command.target);
      break;
    case 'attackMove':
      handleAttackMove(state, ctx, playerId, command.unitIds, command.target);
      break;
    case 'attack':
      handleAttack(
        state,
        ctx,
        playerId,
        command.unitIds,
        command.targetId,
        command.target,
        command.queue === true
      );
      break;
    case 'unfocus':
      handleUnfocus(state, ctx, playerId, command.unitIds, command.targetId);
      break;
    case 'spawn':
      trySpawnUnit(state, ctx, playerId, command.cityId, command.unitType);
      break;
    case 'cancelTraining':
      cancelTraining(state, playerId, command.cityId, command.trainingId);
      break;
    case 'place':
      handlePlace(state, ctx, playerId, command.buildingType, command.target);
      break;
    case 'foundCity':
      handleFoundCity(state, ctx, playerId, command.unitId);
      break;
    case 'foundAt':
      handleFoundAt(state, ctx, playerId, command.unitId, command.target);
      break;
    case 'watch':
      handleWatch(state, ctx, playerId, command.buildingId, command.target);
      break;
    case 'setWorkFocus':
      handleSetWorkFocus(state, ctx, playerId, command.cityId, command.focus);
      break;
    case 'setWorkTargets':
      handleSetWorkTargets(state, ctx, playerId, command.cityId, command.targets);
      break;
    case 'assign':
      handleAssign(state, ctx, playerId, command.unitIds, command.target, command.pin);
      break;
    case 'setHome':
      handleSetHome(state, ctx, playerId, command.unitIds, command.cityId);
      break;
    case 'upgrade':
      handleUpgrade(state, ctx, playerId, command.buildingId);
      break;
    case 'cancel':
      handleCancel(state, ctx, playerId, command.buildingId);
      break;
    case 'cancelUpgrade':
      cancelUpgrade(state, ctx, playerId, command.buildingId);
      break;
    case 'automate':
      handleAutomate(state, ctx, playerId, command.unitIds);
      break;
    case 'group':
      handleGroup(state, playerId, command.unitIds);
      break;
    case 'ungroup':
      handleUngroup(state, playerId, command.unitIds);
      break;
    case 'deploy':
      handleDeploy(state, ctx, playerId, command.unitIds);
      break;
    case 'setStance':
      handleSetStance(state, ctx, playerId, command.unitIds, command.stance);
      break;
    case 'train':
      if ('unitIds' in command) {
        for (const id of new Set(command.unitIds)) handleTrain(state, ctx, playerId, id);
      } else handleTrain(state, ctx, playerId, command.unitId);
      break;
    case 'promote':
      if ('unitIds' in command) handlePromote(state, ctx, playerId, command.unitIds, command.to);
      else {
        handlePromoteInCity(
          state,
          ctx,
          playerId,
          command.cityId,
          command.to,
          command.from,
          command.count
        );
      }
      break;
    case 'blend':
      handleBlend(state, ctx, playerId, command.unitId);
      break;
    case 'expose':
      handleExpose(state, ctx, playerId, command.unitId, dt);
      break;
    case 'resign':
      handleResign(state, playerId);
      break;
    case 'endMatch':
      // Applied by the victory system, after the tick's other systems, so
      // the score is the one the end screen shows.
      break;
  }
}

export { applyCommands };
